import React from "react";
import {
  Box,
  ContentLayout,
  Spinner,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import CodeView from "@amzn/awsui-code-view/code-view";
import ExpandableSectionWithCopy from "../common/ExpandableSectionWithCopy";
import { summarizeHerdStackTrace } from "../../helpers/errorUtils";

export interface HerdErrorContentProps {
  herdState?: string;
  fullDetail?: string;
  isLoadingDetail: boolean;
  detailFailed: boolean;
  expandAll: boolean;
}

function renderDetailBody(
  fullDetail: string | undefined,
  isLoadingDetail: boolean,
  detailFailed: boolean
) {
  if (fullDetail) {
    return <CodeView content={fullDetail} />;
  }
  if (isLoadingDetail) {
    return (
      <Box color="text-status-inactive">
        <SpaceBetween direction="horizontal" size="xs">
          <Spinner />
          <span>Loading full Herd error detail…</span>
        </SpaceBetween>
      </Box>
    );
  }
  if (detailFailed) {
    return (
      <Box color="text-status-error">
        Could not load the full Herd error detail. Use “Open in Herd” to view it
        in ORCA Studio.
      </Box>
    );
  }
  return (
    <Box color="text-status-inactive">
      No further Herd error detail available.
    </Box>
  );
}

const HerdErrorContent: React.FC<HerdErrorContentProps> = ({
  herdState,
  fullDetail,
  isLoadingDetail,
  detailFailed,
  expandAll,
}) => {
  const rootCause = summarizeHerdStackTrace(fullDetail);

  return (
    <ContentLayout>
      <SpaceBetween direction="vertical" size="l">
        {herdState && (
          <Box>
            <Box variant="awsui-key-label">Herd failure state</Box>
            <CodeView content={herdState} />
          </Box>
        )}

        {rootCause && (
          <Box>
            <Box variant="awsui-key-label">Root cause</Box>
            <Box color="text-status-error">
              <CodeView content={rootCause} />
            </Box>
          </Box>
        )}

        <ExpandableSectionWithCopy
          title="USL / Herd error detail"
          copyText={fullDetail}
          expanded={expandAll}
        >
          {renderDetailBody(fullDetail, isLoadingDetail, detailFailed)}
        </ExpandableSectionWithCopy>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default HerdErrorContent;
